export const createPhaseActionGuard = (gameState) => {
  const currentPlayer = gameState.players[gameState.currentPlayerIndex];
  return {
    round: gameState.round,
    currentPlayerId: currentPlayer?.id,
    phase: gameState.phase,
    warPhaseStage: gameState.warPhaseStage,
  };
};

export const isStalePhaseAction = (action, gameState) => {
  const payload = action?.payload;
  if (!payload) return false;

  const {round, currentPlayerId, phase, warPhaseStage} = payload;
  const currentPlayer = gameState.players[gameState.currentPlayerIndex];

  if (round !== undefined && round !== gameState.round) return true;
  if (currentPlayerId !== undefined && currentPlayerId !== currentPlayer?.id) return true;
  if (phase !== undefined && phase !== gameState.phase) return true;
  if (warPhaseStage !== undefined && warPhaseStage !== gameState.warPhaseStage) return true;

  return false;
};

// Имена полей слепка в payload — по ним networkMiddleware понимает, что экшен
// уже несет слепок, созданный в месте диспатча, и не подмешивает свой
export const PHASE_ACTION_GUARD_FIELDS = ['round', 'currentPlayerId', 'phase', 'warPhaseStage'];

/**
 * Начался ли новый отрезок хода: слепки сняты до и после каких-то действий, и
 * хоть одно измерение хода изменилось.
 *
 * ЭТАП фазы войны — такое же новое окно, как фаза или ходящий игрок: у каждого
 * этапа свой дедлайн (getPhaseDurationMs). Сравнение «на глаз», по паре полей,
 * уже приводило к проду: серверные авто-действия закрывали этап НАЙМА и
 * открывали ПЕРЕМЕЩЕНИЕ, не меняя ни фазы, ни текущего игрока, — таймер хода
 * не перезаводился, и перемещение доедало остаток 40-секундного окна найма.
 * Поэтому сравниваем слепок целиком: новое измерение нельзя будет забыть.
 */
export const checkIfTurnContextChanged = (guardBefore, guardAfter) =>
  PHASE_ACTION_GUARD_FIELDS.some((field) => guardBefore?.[field] !== guardAfter?.[field]);

// Экшены завершения ходов: слепок создается в месте диспатча, а проверяется
// в редьюсере (см. isStalePhaseAction в sharedStateReducer) — поздний дубль
// завершения схлопывается no-op'ом у сервера и остальных клиентов, а его
// отправителю сервер тихо досылает актуальное состояние (см.
// checkIfStaleCompletionAction).
// Серверный отказ RELAY_ACTION (shouldRejectStaleRelayedAction) их поэтому
// не касается: отказ с принудительной актуализацией состояния показал бы
// игроку оверлей обрыва связи за штатную гонку «нажал завершение в момент
// срабатывания таймера»
export const PHASE_COMPLETION_ACTION_TYPES = [
  'COMPLETE_PHASE',
  'START_NEXT_PHASE',
  'SET_WAR_PHASE_STAGE',
  'CAPTURE_CITIES',
  // Применение урона закрывает этап ближнего боя, и закрыть его могут двое:
  // клиент последнего подтвердившего и сервер (одновременные подтверждения,
  // см. checkIfCombatStageReadyToComplete)
  'APPLY_MELEE_DAMAGE',
  // Применение отложенных действий этапа — первый шаг его закрытия
  // (handleCompleteTurn), в одной пачке со сменой этапа. Если этап уже закрыл
  // серверный таймер, у сервера они не отбрасываются с оверлеем, а тихо
  // пропускаются (checkIfStaleCompletionAction) — применившись в новом
  // контексте, они зацепили бы отложенные действия уже следующего игрока.
  // Проверки в редьюсере им не нужно: устаревший экшен сервер не применяет и
  // не рассылает, а у отправителя слепка в экшене нет
  'APPLY_RECRUITMENT_ACTIONS',
  'APPLY_MOVEMENT_ACTIONS',
  'APPLY_SHOOTING_ACTIONS',
  'APPLY_MANEUVER_ACTIONS',
  // Завершение своей части одновременной фазы снабжения: фазу мог уже закрыть
  // серверный таймер, и нажатие «завершить» в этот момент — штатная гонка, а не
  // повод показывать оверлей обрыва связи
  'COMPLETE_PLAYER_SUPPLY',
];

// Экшены вне контекста хода: их можно совершать в любой момент партии, и
// смена раунда/фазы/ходящего игрока их не обесценивает. Слепок в них не
// подмешивается (networkMiddleware), и сервер их как устаревшие не отбрасывает.
// Чат матча пишут в чужой ход — отказ с принудительной актуализацией состояния
// показал бы оверлей обрыва связи и стер бы у отправителя его же сообщение
export const TURN_CONTEXT_FREE_ACTION_TYPES = [
  'ADD_CHAT_MESSAGE',
];

/**
 * Решение серверного RELAY_ACTION: отбросить ли экшен как отправленный из уже
 * неактуального контекста хода (клиент совершил действие до того, как получил
 * серверное завершение хода по таймеру, — раунд/фаза/этап/ходящий игрок на
 * сервере уже другие). Отброшенный экшен не применяется и не ретранслируется,
 * а отправителю принудительно доотправляется свежее состояние матча.
 * Экшены без слепка (старые клиенты) не отбрасываются никогда.
 */
export const shouldRejectStaleRelayedAction = (action, gameState) => {
  if (PHASE_COMPLETION_ACTION_TYPES.includes(action?.type)
    || TURN_CONTEXT_FREE_ACTION_TYPES.includes(action?.type)) {
    return false;
  }
  return isStalePhaseAction(action, gameState);
};

/**
 * Устаревший экшен завершения хода/этапа: закрытие уже состоялось на сервере
 * другим путем (таймер хода, серверное доведение ближнего боя), а клиент
 * прислал свое, собранное по прежнему состоянию. Завершения в редьюсерах и так
 * схлопываются no-op'ом (isStalePhaseAction), а применение отложенных действий
 * этапа (APPLY_*_ACTIONS) зацепило бы уже новый контекст, поэтому сервер их
 * не применяет, не рассылает и не перезапускает из-за них таймер хода. Но
 * отправитель у себя УЖЕ применил свое закрытие, и если в нем была
 * случайность (новая политика рынка в START_NEXT_PHASE), сам он с остальными
 * не сойдется — такому отправителю сервер тихо досылает актуальное состояние.
 * Экшены без слепка (старые клиенты) устаревшими не считаются.
 */
export const checkIfStaleCompletionAction = (action, gameState) =>
  PHASE_COMPLETION_ACTION_TYPES.includes(action?.type) && isStalePhaseAction(action, gameState);
